import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";

import CommonTable from "../../common/CommonTable";
import TablePagination from "../../common/tablePagination";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import CancelButton from "../../common/button/cancelButton";
import masterActions from "../../redux/master/actions";
import refundActions from "../../redux/refund/actions";
import MarkRefundedDialog from "./MarkRefundedDialog";
import BulkRefundUploadDialog from "./BulkRefundUploadDialog";

// The worklist is a day's work, not a report -- 25 rows is roughly a screen of scrolling and
// keeps the page count meaningful. The shared itemsPerPage (5) exists for tables that slice a
// full result set on the client; this one is paged by the API.
const ROWS_PER_PAGE = 25;

const EMPTY_FILTERS = { courseid: "", search: "", fromdate: "", todate: "" };

export default function ManualRefund() {
    const dispatch = useDispatch();

    const { courseList } = useSelector((state) => state.masterReducer);
    const { refundList, refundPagination } = useSelector((state) => state.refundReducer);

    // Two copies of the filters on purpose. `filters` is what the user is typing; `appliedFilters`
    // is what the table currently shows. Paging and Export read the applied set, so page 2 and the
    // exported file can never come from different criteria than the rows on screen.
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [page, setPage] = useState(1);
    const [dateError, setDateError] = useState("");
    // Bumped on every Search/Reset so the fetch runs even when the criteria did not change.
    // Without it, clicking Search a second time is a no-op (same state, no re-render) -- and on a
    // worklist that other people are also working through, "refresh" is exactly what that click
    // means.
    const [refreshKey, setRefreshKey] = useState(0);

    const [selected, setSelected] = useState([]);
    const [markOpen, setMarkOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);

    useEffect(() => {
        dispatch({ type: masterActions.GET_COURSE_LIST });
    }, [dispatch]);

    useEffect(() => {
        dispatch({
            type: refundActions.GET_REFUND_LIST,
            payload: { ...appliedFilters, page, limit: ROWS_PER_PAGE }
        });
        // Whatever was ticked belonged to the rows that were on screen. Once the page or the
        // filters change those rows are gone, and carrying the selection forward would let the
        // user confirm refunds they can no longer see.
        setSelected([]);
    }, [dispatch, appliedFilters, page, refreshKey]);

    const onChangeFilter = (field) => (e) => {
        setFilters((prev) => ({ ...prev, [field]: e.target.value }));
        setDateError("");
    };

    const handleSearch = () => {
        if (filters.fromdate && filters.todate && filters.todate < filters.fromdate) {
            setDateError("To date must not be earlier than From date");
            return;
        }
        setPage(1);
        setAppliedFilters(filters);
        setRefreshKey((key) => key + 1);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setDateError("");
        setPage(1);
        setAppliedFilters(EMPTY_FILTERS);
        setRefreshKey((key) => key + 1);
    };

    const handleExport = () => {
        dispatch({ type: refundActions.EXPORT_REFUND_LIST, payload: appliedFilters });
    };

    const handlePageChange = (event, value) => setPage(value);

    const rows = Array.isArray(refundList) ? refundList : [];
    const allSelectedOnPage = rows.length > 0 && selected.length === rows.length;

    const toggleRow = (transactionsid) => {
        setSelected((prev) =>
            prev.includes(transactionsid)
                ? prev.filter((id) => id !== transactionsid)
                : [...prev, transactionsid]
        );
    };

    const selectedRows = rows.filter((row) => selected.includes(row.transactionsid));

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={(e) =>
                        setSelected(e.target.checked ? rows.map((row) => row.transactionsid) : [])
                    }
                />
            ),
            dataIndex: "checkbox",
            render: (_, record) => (
                <input
                    type="checkbox"
                    checked={selected.includes(record.transactionsid)}
                    onChange={() => toggleRow(record.transactionsid)}
                />
            )
        },
        { name: "Sr.No", dataIndex: "srNo" },
        { name: "Candidate Name", dataIndex: "candidatename" },
        { name: "Course Name", dataIndex: "coursename" },
        { name: "Email ID", dataIndex: "emailid" },
        { name: "Mobile No", dataIndex: "mobileno" },
        { name: "Receipt No", dataIndex: "receiptno" },
        { name: "Payment ID", dataIndex: "paymentid" },
        { name: "Gateway", dataIndex: "gateway" },
        { name: "Total Amount", dataIndex: "totalamount" },
        { name: "Order Date", dataIndex: "orderdate" },
        { name: "Refund Reason", dataIndex: "refundreason" }
    ];

    // Sr.No continues across pages rather than restarting at 1, so a row the user refers to on
    // page 3 is the same row when they come back to it.
    const tableData = rows.map((row, index) => ({
        ...row,
        srNo: (page - 1) * ROWS_PER_PAGE + index + 1,
        paymentid: row.paymentid || "-",
        refundreason: row.refundreason || "-",
        gateway: row.gateway || "-"
    }));

    return (
        <>
            <Box
                sx={{
                    margin: "1rem auto",
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    borderRadius: "8px",
                    padding: "10px"
                }}
            >
                <Card sx={{ p: 2 }}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl sx={{ width: "100%" }} size="small">
                                <InputLabel id="refund-course-label">Course</InputLabel>
                                <Select
                                    labelId="refund-course-label"
                                    label="Course"
                                    size="small"
                                    value={filters.courseid}
                                    onChange={onChangeFilter("courseid")}
                                >
                                    <MenuItem value="">All Courses</MenuItem>
                                    {courseList?.all
                                        ?.filter((course) => course.isactive === "1")
                                        ?.map((course, i) => (
                                            <MenuItem key={i} value={course.courseid}>
                                                {course.coursename}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search name / email / mobile"
                                value={filters.search}
                                onChange={onChangeFilter("search")}
                                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Order Date From"
                                InputLabelProps={{ shrink: true }}
                                value={filters.fromdate}
                                onChange={onChangeFilter("fromdate")}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Order Date To"
                                InputLabelProps={{ shrink: true }}
                                value={filters.todate}
                                onChange={onChangeFilter("todate")}
                                error={!!dateError}
                                helperText={dateError}
                            />
                        </Grid>

                        <Grid item xs={12} md={2} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                            <BluePrimaryButton title="Search" handleSubmit={handleSearch} />
                            <CancelButton title="Reset" handleBack={handleReset} />
                        </Grid>
                    </Grid>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 3
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {refundPagination?.total || 0} candidate(s) awaiting refund
                            {selected.length > 0 ? ` • ${selected.length} selected` : ""}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <MauvelousPrimaryButton
                                title="Export CSV"
                                icon={<MdOutlineFileDownload size={18} />}
                                handleClick={handleExport}
                                disabled={!rows.length}
                            />
                            <MauvelousPrimaryButton
                                title="Upload CSV"
                                icon={<MdOutlineFileUpload size={18} />}
                                handleClick={() => setUploadOpen(true)}
                            />
                        </Box>
                    </Box>

                    <CommonTable columnData={columns} tableData={tableData} />

                    <Box display="flex" justifyContent="flex-end" sx={{ marginRight: 2 }}>
                        <MauvelousPrimaryButton
                            title="Mark as Refunded"
                            handleClick={() => setMarkOpen(true)}
                            disabled={selected.length === 0}
                        />
                    </Box>
                </Card>

                {refundPagination?.totalpages > 1 && (
                    <TablePagination
                        pageCount={refundPagination.totalpages}
                        handlePageChange={handlePageChange}
                        currentPage={page}
                    />
                )}
            </Box>

            <MarkRefundedDialog
                open={markOpen}
                rows={selectedRows}
                filters={{ ...appliedFilters, page, limit: ROWS_PER_PAGE }}
                onClose={() => setMarkOpen(false)}
            />

            <BulkRefundUploadDialog
                open={uploadOpen}
                filters={{ ...appliedFilters, page, limit: ROWS_PER_PAGE }}
                onClose={() => setUploadOpen(false)}
            />
        </>
    );
}
